'use client'

import Link from 'next/link'
import { Section, SectionHeading } from '@/components/platform/v2/Section'
import { ProductHero, PlatformMap, Statement, FlowStrip, ProductCta } from '@/components/platform/v2/ProductKit'
import { LEVELS } from '@/data/platform'
import { DEMO, HOME_PLANS } from '@/data/pricing'
import { ArrowRight } from 'lucide-react'

const SOURCES = [
    'Сайт',
    'Приложение',
    'Офлайн',
    'Телефон',
    'Мессенджер',
    'Мероприятие',
    'Внешний сервис',
    'Маркетплейсы',
    'Социальные сети',
]

const RESULTS = [
    'Клиент',
    'CRM',
    'Заявка',
    'Сделка',
    'Задача',
    'Заказ',
    'Оплата',
    'Чат',
    'Документы',
    'Лояльность',
    'Автоматизация',
    'Аналитика',
]

const PlatformPageComponent = () => {
    return (
        <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
            <ProductHero
                eyebrow="VCENTRE.ONE"
                titleLines={['Весь путь клиента', 'в одной системе.']}
                lead="Начните с готовой системы управления и стандартного приложения бизнеса. Расширяйте клиентские каналы, расширения и AI, сохраняя общие CRM, каталог и данные онлайн- и офлайн-клиентов."
                primary={{ label: 'Собрать VCENTRE.ONE под мой бизнес', to: '/signin?source=platform' }}
                secondary={{ label: 'Посмотреть, как всё связано', to: '#contour' }}
                visual={
                    <img
                        src={'/assets/images/platform/platform-mascot-hero.png'}
                        alt="Маскот VCENTRE.IT с фразой «Всё связано»"
                        width={1254}
                        height={1254}
                        loading="eager"
                        className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
                    />
                }
            />

            {/* ГЛАВНАЯ СХЕМА */}
            <Section tone="quiet" id="contour">
                <SectionHeading
                    eyebrow="Единый контур"
                    title="Откуда приходит клиент и что происходит дальше"
                    text="Каналов может быть много. Система, в которой бизнес продолжает работу, — одна."
                />

                <div className="mt-10 grid gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:gap-6">
                    <div className="rounded-[1.5rem] border border-border/60 bg-[hsl(var(--deep-black))] p-5">
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Откуда приходит клиент</p>
                        <div className="mt-4 flex flex-col gap-2">
                            {SOURCES.map((s) => (
                                <div
                                    key={s}
                                    className="flex items-center justify-between gap-3 rounded-xl border border-border/50 bg-[hsl(var(--charcoal))] px-3.5 py-2.5"
                                >
                                    <span className="text-sm font-semibold text-foreground/90">{s}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-center py-2">
                        <div className="flex w-full max-w-[240px] flex-col items-center gap-3 rounded-[1.5rem] border border-primary/40 bg-primary/10 px-6 py-8 text-center lg:w-auto">
                            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary/80">Центр</span>
                            <span className="text-xl font-extrabold tracking-[-0.02em] text-primary sm:text-2xl">VCENTRE.ONE</span>
                            <span className="text-xs text-foreground/70">Один контур между клиентом и бизнесом</span>
                        </div>
                    </div>

                    <div className="rounded-[1.5rem] border border-border/60 bg-[hsl(var(--deep-black))] p-5">
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Что происходит дальше</p>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {RESULTS.map((r) => (
                                <span
                                    key={r}
                                    className="rounded-full border border-border/50 bg-[hsl(var(--charcoal))] px-3.5 py-2 text-sm text-foreground/90"
                                >
                                    {r}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </Section>

            {/* НЕВАЖНО, ГДЕ ПОЯВИЛСЯ КЛИЕНТ */}
            <Section>
                <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center">
                    <div className="flex flex-col gap-5">
                        <p className="eyebrow">Онлайн и офлайн</p>
                        <h2 className="display-2">Неважно, где появился клиент</h2>
                        <div className="flex flex-col gap-2 text-base text-foreground/85 sm:text-lg">
                            <p>Клиент может установить приложение.</p>
                            <p>Может оставить заявку на сайте.</p>
                            <p>Может позвонить.</p>
                            <p>Может прийти в офис.</p>
                            <p>А менеджер может вообще создать его вручную.</p>
                        </div>
                        <p className="body-text max-w-xl">
                            Для VCENTRE.ONE это всё равно клиент, с которым бизнес продолжает работать в одной системе.
                        </p>
                    </div>
                    <FlowStrip
                        steps={[
                            { title: 'Клиент появился', note: 'любой канал или ручное создание' },
                            { title: 'Появился профиль', note: 'контакты и история' },
                            { title: 'Работа продолжается', note: 'заявка, сделка, задача, коммуникация', accent: true },
                        ]}
                    />
                </div>
            </Section>

            {/* ТРИ СЛОЯ */}
            <Section tone="quiet">
                <SectionHeading eyebrow="Структура" title="Три слоя VCENTRE.ONE" text="Система управления и стандартное приложение доступны с BASE. Дополнительные клиентские расширения и расширенные каналы — со старших тарифов." />
                <div className="mt-10 grid gap-5 lg:grid-cols-3">
                    {LEVELS.map((level) => (
                        <article key={level.title} className="surface flex flex-col gap-3">
                            <span className="text-3xl font-extrabold text-primary/70">{level.index}</span>
                            <h3 className="display-3">{level.title}</h3>
                            <p className="body-text">{level.lead}</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {level.items.map((i) => (
                                    <span key={i} className="rounded-full border border-border/50 px-3 py-1 text-xs text-foreground/80">
                                        {i}
                                    </span>
                                ))}
                            </div>
                        </article>
                    ))}
                </div>
            </Section>

            <Section>
                <SectionHeading eyebrow="Развитие" title="Готовая основа и четыре уровня возможностей" />
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {HOME_PLANS.map((plan) => (
                        <div key={plan.key} className="border-t border-primary/50 py-5">
                            <h3 className="display-3">{plan.name}</h3>
                            <p className="body-text mt-3">{plan.meaning}</p>
                        </div>
                    ))}
                </div>
                <div className="surface-quiet mt-6">
                    <h3 className="font-bold text-primary">{DEMO.title}</h3>
                    <p className="body-text mt-3">{DEMO.text}</p>
                    <Link href="/signin?source=platform-demo" className="btn-link mt-4">Попробовать DEMO</Link>
                </div>
            </Section>

            {/* СИЛЬНЫЙ ТЕЗИС */}
            <Section id="ecosystem" tone="quiet">
                <SectionHeading eyebrow="Экосистема VCENTRE.IT" title="VCENTRE.ONE — центральная платформа и главный рабочий инструмент бизнеса" text="Расширения добавляют механики внутри ONE. Платформы решают специализированные задачи самостоятельно. Магазин объединяет ресурсы на уровне аккаунта VCENTRE." />
                <div className="surface mt-8">
                    <h3 className="display-3">Расширения</h3>
                    <p className="body-text mt-3">Подключайте дополнительные бизнес-механики, когда они нужны вашему сценарию. Клиенты, CRM, коммуникации и данные остаются общими.</p>
                    <Link href="/platform/modules" className="btn-link mt-4">Смотреть расширения</Link>
                </div>
                <div className="mt-8 flex flex-wrap justify-center gap-4">
                    <Link href="/platforms" className="btn-ghost">Платформы VCENTRE.IT</Link>
                    <Link href="/store" className="btn-ghost">Магазин VCENTRE</Link>
                </div>
            </Section>

            <Section>
                <Statement note="Все клиенты. Все каналы. Одна платформа.">
                    Не ещё один сервис.
                    <br />
                    <span className="text-primary">Один контур между клиентом и бизнесом.</span>
                </Statement>
                <div className="mt-6 text-center">
                    <Link href="/signin?source=platform&focus=process" className="btn-link">
                        Показать мой процесс <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </Section>

            {/* НАВИГАЦИЯ ПО ПЛАТФОРМЕ */}
            <Section tone="quiet">
                <SectionHeading
                    eyebrow="Карта платформы"
                    title="Разберите контур по частям"
                    text="Каждый раздел объясняет свою часть системы — и показывает, как она связана с остальными."
                />
                <div className="mt-10">
                    <PlatformMap activeTo="/platform" />
                </div>
                <div className="mt-8 flex justify-center">
                    <Link href="/platform/pricing" className="btn-link">
                        Тарифы и что входит <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </Section>

            <Section>
                <ProductCta
                    title="Соберите свой контур VCENTRE.ONE"
                    text="Настройте готовую систему и приложение под свой бренд. Выберите, какие каналы расширять и какие расширения и AI нужны вашему бизнесу."
                    buttonLabel="Собрать решение"
                    to="/signin?source=platform-bottom"
                    secondary={{ to: '/platform/cases', label: 'Смотреть кейсы' }}
                    visual={
                        <img
                            src={'/assets/images/platform/platform-mascot-cta.png'}
                            alt="Маскот VCENTRE.IT с ракетой и фразой «Запускаем быстро»"
                            width={1254}
                            height={1254}
                            loading="lazy"
                            className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
                        />
                    }
                />
            </Section>
        </div>
    )
}

export default PlatformPageComponent
